/**
 * OrderItemsTable Component
 *
 * Displays and manages order line items with inline editing.
 * Includes product search autocomplete for adding new items.
 * @see US-8.15 - Formulaire Creation Commande
 */

import { useCallback, useMemo } from 'react';
import { Trash2, AlertCircle, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCentimes } from '@/lib/format';
import { useCurrency } from '@/hooks';
import { Select } from '@/components/ui';
import { ProductSearchAutocomplete } from './ProductSearchAutocomplete';
import type { SupplierCatalogItemWithDetails } from '@bstock/shared';

export interface OrderLineItem {
  /** Temporary ID for new items not yet saved */
  tempId?: string;
  /** Database ID for existing items */
  id?: string;
  /** Catalog item ID */
  catalogItemId: string;
  /** Raw product ID */
  rawProductId: string;
  /** Product name (snapshot) */
  rawProductName: string;
  /** Packaging name */
  packagingName: string;
  /** Conversion rate to recipe unit */
  conversionToRecipeUnit: number;
  /** Recipe unit symbol */
  recipeUnitSymbol: string;
  /** Ordered quantity */
  quantity: number;
  /** Unit price in centimes */
  unitPriceExclTax: number;
  /** Available packaging options for this product */
  packagingOptions?: {
    catalogItemId: string;
    packagingName: string;
    unitPriceExclTax: number;
    conversionToRecipeUnit: number;
  }[];
}

interface OrderItemsTableProps {
  /** Current order items */
  items: OrderLineItem[];
  /** Callback when items change */
  onChange: (items: OrderLineItem[]) => void;
  /** Supplier ID for product search */
  supplierId: string | null;
  /** Whether the form is read-only */
  readOnly?: boolean;
  /** CSS class name */
  className?: string;
}

export function OrderItemsTable({
  items,
  onChange,
  supplierId,
  readOnly = false,
  className,
}: OrderItemsTableProps) {
  const currency = useCurrency();

  // Calculate total
  const totalExclTax = useMemo(() => {
    return items.reduce((sum, item) => sum + item.quantity * item.unitPriceExclTax, 0);
  }, [items]);

  // Add new item from catalog — variants are passed by the autocomplete
  const handleAddItem = useCallback(
    (catalogItem: SupplierCatalogItemWithDetails, variants: SupplierCatalogItemWithDetails[]) => {
      const packagingOptions = variants.map((ci) => ({
        catalogItemId: ci.id,
        packagingName: ci.packaging.name,
        unitPriceExclTax: ci.currentPrice?.priceExclTax ?? 0,
        conversionToRecipeUnit: ci.conversionToRecipeUnit,
      }));

      const newItem: OrderLineItem = {
        tempId: `temp-${Date.now()}-${Math.random()}`,
        catalogItemId: catalogItem.id,
        rawProductId: catalogItem.rawProductId,
        rawProductName: catalogItem.rawProduct.name,
        packagingName: catalogItem.packaging.name,
        conversionToRecipeUnit: catalogItem.conversionToRecipeUnit,
        recipeUnitSymbol: catalogItem.rawProduct.recipeUnit.symbol,
        quantity: 1,
        unitPriceExclTax: catalogItem.currentPrice?.priceExclTax ?? 0,
        packagingOptions,
      };

      onChange([...items, newItem]);
    },
    [items, onChange]
  );

  // Update item quantity
  const handleQuantityChange = useCallback(
    (index: number, quantity: number) => {
      if (quantity < 1 || quantity > 1000) return;
      const updated = [...items];
      updated[index] = { ...updated[index], quantity };
      onChange(updated);
    },
    [items, onChange]
  );

  // Update item packaging (changes price too)
  const handlePackagingChange = useCallback(
    (index: number, catalogItemId: string) => {
      const item = items[index];
      const packaging = item.packagingOptions?.find((p) => p.catalogItemId === catalogItemId);
      if (!packaging) return;

      const updated = [...items];
      updated[index] = {
        ...updated[index],
        catalogItemId,
        packagingName: packaging.packagingName,
        unitPriceExclTax: packaging.unitPriceExclTax,
        conversionToRecipeUnit: packaging.conversionToRecipeUnit,
      };
      onChange(updated);
    },
    [items, onChange]
  );

  // Remove item
  const handleRemoveItem = useCallback(
    (index: number) => {
      const updated = items.filter((_, i) => i !== index);
      onChange(updated);
    },
    [items, onChange]
  );

  return (
    <div className={cn('space-y-4', className)}>
      {/* Add product autocomplete */}
      {!readOnly && (
        <ProductSearchAutocomplete
          supplierId={supplierId}
          onSelect={handleAddItem}
          placeholder="Rechercher un produit a ajouter..."
        />
      )}

      {/* Table */}
      <div className="border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-neutral-700">
                Produit
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-neutral-700 w-48">
                Conditionnement
              </th>
              <th className="text-center px-4 py-3 text-sm font-medium text-neutral-700 w-24">
                Qté
              </th>
              <th className="text-right px-4 py-3 text-sm font-medium text-neutral-700 w-24">
                PU HT
              </th>
              <th className="text-right px-4 py-3 text-sm font-medium text-neutral-700 w-40">
                Total
              </th>
              {!readOnly && (
                <th className="w-10 px-1"></th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={readOnly ? 5 : 6} className="px-4 py-8 text-center">
                  <div className="flex flex-col items-center gap-2 text-neutral-400">
                    <Package className="w-8 h-8" />
                    <span className="text-sm">Aucun produit ajoute</span>
                    {!readOnly && (
                      <span className="text-xs">
                        Utilisez le champ ci-dessus pour ajouter des produits
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              items.map((item, index) => {
                const lineTotal = item.quantity * item.unitPriceExclTax;
                const hasPackagingOptions = (item.packagingOptions?.length ?? 0) > 0;

                return (
                  <tr
                    key={item.id || item.tempId}
                    className="border-b border-neutral-100 last:border-0"
                  >
                    {/* Product name */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-dark-500">{item.rawProductName}</div>
                      <div className="text-xs text-neutral-400 mt-0.5">
                        = {item.quantity * item.conversionToRecipeUnit} {item.recipeUnitSymbol}
                      </div>
                    </td>

                    {/* Packaging */}
                    <td className="px-4 py-3">
                      {readOnly || !hasPackagingOptions ? (
                        <span className="text-sm text-neutral-600">{item.packagingName}</span>
                      ) : (
                        <Select
                          value={item.catalogItemId}
                          onChange={(value) => handlePackagingChange(index, value)}
                          options={item.packagingOptions?.map((p) => ({
                            value: p.catalogItemId,
                            label: `${p.packagingName} (${p.conversionToRecipeUnit} ${item.recipeUnitSymbol})`,
                          }))}
                          className="text-sm"
                        />
                      )}
                    </td>

                    {/* Quantity */}
                    <td className="px-4 py-3">
                      {readOnly ? (
                        <div className="text-center text-sm">{item.quantity}</div>
                      ) : (
                        <input
                          type="number"
                          min="1"
                          max="1000"
                          value={item.quantity}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 1;
                            handleQuantityChange(index, Math.min(val, 1000));
                          }}
                          className={cn(
                            'w-full h-9 px-2 text-center rounded-lg border border-neutral-300 text-sm',
                            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                          )}
                        />
                      )}
                    </td>

                    {/* Unit price */}
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-neutral-600">
                        {formatCentimes(item.unitPriceExclTax, currency)}
                      </span>
                    </td>

                    {/* Line total */}
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-dark-500">
                        {formatCentimes(lineTotal, currency)}
                      </span>
                    </td>

                    {/* Delete button */}
                    {!readOnly && (
                      <td className="px-1 py-3">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="p-1 rounded-lg text-neutral-400 hover:text-danger-500 hover:bg-danger-50 transition-colors"
                          aria-label="Supprimer la ligne"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
          {/* Footer with total */}
          <tfoot className="bg-neutral-50 border-t border-neutral-200">
            <tr>
              <td colSpan={4} className="px-4 py-3 text-right">
                <span className="text-sm font-medium text-neutral-700">Total HT</span>
              </td>
              <td className="px-4 py-3 text-right">
                <span className="text-lg font-semibold text-dark-500">
                  {formatCentimes(totalExclTax, currency)}
                </span>
              </td>
              {!readOnly && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Empty warning */}
      {!readOnly && items.length === 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-warning-50 border border-warning-200 rounded-lg text-sm text-warning-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Ajoutez au moins un produit pour pouvoir envoyer la commande</span>
        </div>
      )}
    </div>
  );
}
